import React, { useState, useEffect, useRef } from 'react';
import { BulbOutlined, CloseOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

/**
 * Top-screen banner notification component for crisis resource suggestions
 * Displays a prominent strip at the top when relevant digital resources are available
 * Uses same styling as TaskAssignmentNotification for consistency
 */
const CrisisResourceSuggestionBanner = ({ 
  resources = [], 
  onDismiss, 
  sessionKey, 
  topOffset = 0,
  bannerId, 
  registerBanner, 
  unregisterBanner, 
  calculatePosition 
}) => {
  const resourceCount = resources?.length || 0;
  
  // Only log when resource count actually changes (use ref to track previous)
  const prevResourceCountRef = useRef(0);
  useEffect(() => {
    if (resourceCount !== prevResourceCountRef.current && resourceCount > 0) {
      console.log(`🎯 CrisisResourceSuggestionBanner - resource count changed: ${prevResourceCountRef.current} -> ${resourceCount}`);
    }
    prevResourceCountRef.current = resourceCount;
  }, [resourceCount]);
  
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [dynamicPosition, setDynamicPosition] = useState(topOffset);
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check if banner was already dismissed for this session
    const dismissed = sessionKey ? sessionStorage.getItem(`banner-dismissed-${sessionKey}`) === 'true' : false;
    // Only log dismissal status if actually dismissed
    if (dismissed) {
      console.log('🎯 CrisisResourceSuggestionBanner - previously dismissed for session:', sessionKey);
    }
    return dismissed;
  });

  // Show banner with slide animation when resources are available
  useEffect(() => {
    if (resources && resources.length > 0 && !isDismissed && bannerId && registerBanner && unregisterBanner && calculatePosition) {
      setIsVisible(true);
      
      // Register this banner as visible
      registerBanner(bannerId);
      
      // Calculate and set dynamic position
      const position = calculatePosition(bannerId);
      setDynamicPosition(position);
      
      return () => {
        // Unregister banner when component unmounts
        unregisterBanner(bannerId);
      };
    } else {
      setIsVisible(false);
      if (bannerId && unregisterBanner) {
        unregisterBanner(bannerId);
      }
    }
  }, [resources, isDismissed, bannerId, registerBanner, unregisterBanner, calculatePosition]);

  // Update position when other banners change
  useEffect(() => {
    if (isVisible && bannerId && calculatePosition) {
      const position = calculatePosition(bannerId);
      const oldPosition = dynamicPosition;
      setDynamicPosition(position);
      // Only log position if it actually changed
      if (position !== oldPosition) {
        console.log(`🎯 CrisisResourceSuggestionBanner: Position updated from ${oldPosition}px to ${position}px`);
      }
    }
  }, [isVisible, bannerId, calculatePosition]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (bannerId && unregisterBanner) {
      unregisterBanner(bannerId);
    }
    setTimeout(() => {
      setIsDismissed(true);
      if (sessionKey) {
        sessionStorage.setItem(`banner-dismissed-${sessionKey}`, 'true');
      }
      if (onDismiss) {
        onDismiss();
      }
    }, 300); // Allow fade-out animation to complete
  };

  const handleViewResources = () => {
    console.log('🔗 Navigating to My Crisis Resources');
    navigate('/my-crisis-resources');
    // Don't auto-dismiss - let user manually close with X button
  };

  // Don't render if dismissed or no resources
  if (isDismissed || !resources || resources.length === 0 || !isVisible) {
    return null;
  }

  const resourceText = resourceCount === 1 ? 'resource' : 'resources';

  return (
    <>
      <style>
        {`
          @keyframes slide-down-resources {
            from {
              transform: translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes slide-up-resources {
            from {
              transform: translateY(0);
              opacity: 1;
            }
            to {
              transform: translateY(-100%);
              opacity: 0;
            }
          }
          
          .resource-banner-enter {
            animation: slide-down-resources 0.4s ease-out;
          }
          
          .resource-banner-exit {
            animation: slide-up-resources 0.3s ease-in;
          }
        `}
      </style>
      
      <div 
        className="resource-banner-enter"
        style={{
          position: 'fixed',
          top: `${dynamicPosition}px`,
          left: 0,
          right: 0,
          zIndex: 9998,
          backgroundColor: '#1890ff',
          color: 'white',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: 'none',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        {/* Left side - Icon and message */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BulbOutlined 
            style={{ 
              fontSize: '18px',
              color: '#ffd700'
            }} 
          />
          <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
            💡 Helpful Crisis Resources Available - {resourceCount} {resourceText} available for this crisis type:{' '}
            <span style={{ fontWeight: 'bold' }}>
              {resources.slice(0, 3).map(resource => resource.name).join(', ')}
              {resourceCount > 3 && ` and ${resourceCount - 3} more`}
            </span>
          </div>
        </div>

        {/* Right side - Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleViewResources}
            style={{
              backgroundColor: 'white',
              color: '#1890ff',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
            }}
          >
            <FileTextOutlined />
            View Resources
          </button>
          
          <button
            onClick={handleDismiss}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '4px',
              padding: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            title="Dismiss notification"
          >
            <CloseOutlined style={{ fontSize: '14px' }} />
          </button>
        </div>
      </div>
    </>
  );
};

export default CrisisResourceSuggestionBanner; 